import React, { FC, useState } from 'react';

import { Icon } from '@iconify/react';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import Button from '~/components/atoms/buttons/Button';
import { StyledTextField } from '~/components/atoms/inputs/TextField';
import { useIsViewport, Viewports } from '~/hooks/useIsViewport';
import {
  CancellationFormDataType,
  FormFields,
} from '~/modules/cancellation/views/variants/v3/types';
import { Typographies } from '~/theme/typography';

import styles from '../styles.module.scss';

type Props = {
  title: string;
  onSubmit: (
    data: Pick<CancellationFormDataType, FormFields.FEEDBACK>,
  ) => Promise<void>;
  onRefuse: () => void;
  onProductSwitch?: () => Promise<void>;
};

const FeedbackStep: FC<Props> = ({
  title,
  onSubmit,
  onRefuse,
  onProductSwitch,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState('');

  const { t } = useTranslation('cancellation');
  const isDesktop = useIsViewport(Viewports.EXTRA_SMALL_DESKTOP);

  const handleChange = (e): void => {
    setFeedback(e.target.value);
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await onSubmit({ feedback });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductSwitch = async (): Promise<void> => {
    if (onProductSwitch) {
      try {
        setIsLoading(true);
        await onProductSwitch();
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div>
      <div className={styles.form__fieldset}>
        <Typography
          variant={Typographies.TITLE_MEDIUM}
          className={styles.width_limit}
          component="h4"
        >
          {title}
        </Typography>

        <StyledTextField
          value={feedback}
          onChange={handleChange}
          className={clsx(styles.textarea, styles.width_limit)}
          multiline
          fullWidth
          rows={8}
          autoComplete="off"
        />
      </div>

      <div className={styles.buttons}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          fullWidth={!isDesktop}
          className={styles.button}
          disabled={isLoading}
        >
          {t('general:continue', 'Continue')}
        </Button>

        {onProductSwitch ? (
          <Button
            color="error"
            variant="contained"
            onClick={handleProductSwitch}
            fullWidth={!isDesktop}
            className={clsx(styles.button, styles['button--discount'])}
          >
            <Icon
              icon="material-symbols:featured-seasonal-and-gifts-rounded"
              className={styles.icon}
            />
            <span>
              {t('modal:downsell_v2:apply_discount', {
                discount: 50,
                defaultValue: 'Apply My {{discount}}% Discount',
              })}
              !
            </span>
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={onRefuse}
            fullWidth={!isDesktop}
            className={styles.button}
            disabled={isLoading}
          >
            {t('general:cancel', 'Cancel')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default FeedbackStep;
